import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Auth } from "aws-amplify";
import Axios from "axios";
import { API_ROUTES } from 'src/config/api';
import { RSelect } from 'src/components/Component';
import Skeleton from 'react-loading-skeleton';
import ProviderNameTransformer from 'src/transformer/provider-name';

import { fetchOrganisations } from 'src/actions/organisationActions';

import { PencilIcon } from '@heroicons/react/24/outline';

import 'react-loading-skeleton/dist/skeleton.css'

const OrganisationInformation = ({ organisation, enums, isAdmin }) => {
  const state = useSelector((state) => state);
  const dispatch = useDispatch();

  const [name, setName] = useState('');
  const [parent, setParent] = useState(null);

  const [editingName, setEditingName] = useState(false);
  const [updatingName, setUpdatingName] = useState(false);

  const [updating, setUpdating] = useState(false);
  const [editing, setEditing] = useState(false);

  const [dropdownOptions, setDropdownOptions] = useState([]);

  const [item, setItem] = useState(null);

  useEffect(() => {
    let temp = null;

    for (const organisationItem of organisation.organisations) {
      if (organisationItem.id === organisation.organisation) {
        temp = organisationItem;;
      }
    }

    setItem(temp);
    setName(temp?.name);
  }, [organisation]);

  useEffect(() => {
    const organisations = state.organisation.organisations;

    let options = organisations.map((item) => ({ value: item.id, label: `${item.name} | ${item.id}` }));
    options.sort((a, b) => a.label.localeCompare(b.label));

    options.forEach(item => {
      if (typeof organisation.parent !== 'undefined' && organisation.parent && item.value === organisation.parent.id) {
        setParent(item);
      }
    });

    setDropdownOptions(options)
  }, [state.organisation.organisations, state.organisation.organisation, organisation]);

  const changeParentOrganisation = async (value) => setParent(value);

  const saveName = async (e) => {
    e.preventDefault();

    setUpdatingName(true);

    const user = await Auth.currentAuthenticatedUser();

    await Axios(
      API_ROUTES.user['organisation:update'](organisation.organisation),
      {
        method: 'post',
        data: {
          name: name,
        },
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
      }
    );

    setUpdatingName(false);
    setEditingName(false);
    await dispatch(fetchOrganisations());
  };

  const saveParent = async (e) => {
    e.preventDefault();

    setUpdating(true);

    const user = await Auth.currentAuthenticatedUser();

    await Axios(
      API_ROUTES.user['organisation:update'](organisation.organisation),
      {
        method: 'post',
        data: {
          parent: parent.value,
        },
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
      }
    );

    setUpdating(false);
    setEditing(false);
    await dispatch(fetchOrganisations());
  };

  if (!item) {
    return <React.Fragment />;
  }

  return (
    <div className='mb-4'>

      <div>

        <h3 className="font-extralight uppercase font-mono text-2xl dark:text-white text-violet-900 mb-4 border-b border-violet-900 pb-2">
          Organisation Information
        </h3>

        <div>
          <table className="table-auto w-full divide-y divide-gray-200">
            <tbody className='divide-y divide-gray-200'>
              <tr>
                <td className="text-gray-700 font-bold pb-2 py-3">Organisation Name</td>
                <td colSpan={updatingName === true ? 2 : 1}>
                  {updatingName === true && <Skeleton height={'29px'} />}
                  {editingName === false && updatingName === false && name}
                  {editingName === true && updatingName === false && (
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      value={name}
                      onChange={(e) => { setName(e.target.value) }}
                    />
                  )}
                </td>
                {isAdmin === true && updatingName === false && (
                  <td className='text-right pr-2'>
                    {editingName === false && (
                      <PencilIcon className="inline h-4 w-4 hover:text-blue-800 text-violet-800 cursor-pointer" onClick={() => setEditingName(true)} />
                    )}
                    {editingName === true && (
                      <button type="submit" onClick={saveName} className="btn btn-md btn-primary bg-violet-800">Save</button>
                    )}
                  </td>
                )}
              </tr>
              <tr>
                <td className="text-gray-700 font-bold pb-2 py-3">Organisation ID</td>
                <td>{item?.id}</td>
              </tr>
              {isAdmin === true && (
                <tr>
                  <td className="text-gray-700 font-bold pb-2 py-3">Parent Account</td>
                  <td colSpan={updating === true ? 2 : 1}>
                    {updating === true && <Skeleton height={'29px'} />}
                    {editing === false && updating === false && (
                      <React.Fragment>{item?.parent ? `${item.parent.name} (${item.parent.id})` : 'NONE'}</React.Fragment>
                    )}
                    {editing === true && updating === false && (
                      <RSelect
                        isClearable
                        name="parent"
                        id="parent"
                        className="form-control form-control-md p-0 border-0 indicator-hidden"
                        value={parent}
                        options={dropdownOptions}
                        onChange={changeParentOrganisation}
                        placeholder=""
                        classNamePrefix="react-select"
                      />
                    )}
                  </td>
                  {isAdmin === true && (
                    <td className='text-right pr-2'>
                      {editing === false && (
                        <PencilIcon className="inline h-4 w-4 hover:text-blue-800 text-violet-800 cursor-pointer" onClick={() => setEditing(true)} />
                      )}
                      {editing === true && (
                        <button type="submit" onClick={saveParent} className="btn btn-md btn-primary bg-violet-800">Save</button>
                      )}
                    </td>
                  )}
                </tr>
              )}
              <tr>
                <td className="text-gray-700 font-bold pb-2 py-3">Active Since</td>
                <td>{moment(item.created_at).format('Do MMMM YYYY')}</td>
              </tr>
              <tr>
                <td className="text-gray-700 font-bold pb-2 py-3">Enabled Payment Providers</td>
                <td>
                  {!item?.integrations?.payments || item?.integrations?.payments.length === 0
                    ? <strong className='text-red-700'>No Payment Providers Setup</strong>
                    : item?.integrations?.payments.map((paymentProvider) => {
                      return ProviderNameTransformer(paymentProvider);
                    }).join(', ')}
                </td>
              </tr>
              {enums?.currency && (
                <tr>
                  <td className="text-gray-700 font-bold pb-2 py-3">Enabled Currencies</td>
                  <td>
                    {!item.currencies || item.currencies.length === 0
                      ? <strong className='text-red-700'>No Payment Providers / Currencies Setup</strong>
                      : item.currencies.map(function (currency) {
                        return enums.currency && typeof enums.currency[currency] !== 'undefined' ? enums.currency[currency].major_unit : currency;
                      }).join(', ')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>


        </div>

      </div>

    </div>
  );
}

export default OrganisationInformation;